import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Switch,
  Route,
  Redirect,
  Link,
  useRouteMatch,
  useHistory,
  useLocation,
} from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import queryString from 'query-string';
import classNames from 'classnames';
import * as Sentry from '@sentry/react';
import {
  useActivityOverview,
  useActivityEvent,
  useTokenizedApi,
  useActivityCount,
  useActivityId,
  useInstrumentWithDetails,
  overviewActivityPendingWorkspaceFilter,
  Instrument,
} from '@tokenized/sdk-react-private';
import { isDebugBuild } from '#config/shared';
import { sentryDSN } from '#config/definitions';
import { useSelectedProfileId } from '#modules/state/ProfileState';
import loadVisible from '#modules/utils/loadVisible';
import { getAppName } from '#app/containers/ThemeLoader';
import * as modalRoutes from '#app/containers/modalRoutes';
import {
  locationToOpenModal,
  locationToShowInstrumentActivity,
  locationToShowContract,
  locationToShowMainPage,
  locationToUpdateMainPage,
  locationToUpdateQueryString,
} from '#app/containers/routes';
import { onSetTab } from '#modules/state/TabState';
import { setShowCancelledActivityAction } from '#modules/state/ActivityState';
import Table from '#components/Elements/Table/Table';
import TabHeader from '#components/Elements/Tabs/TabHeader';
import Tabs from '#components/Elements/Tabs/Tabs';
import TabBadge from '#components/Elements/Tabs/TabBadge';
import Icon from '#components/Elements/Icon/Icon';
import ContractEditingIcon from '#components/Elements/SVGIcon/ContractEditingIcon';
import ContractCrossIcon from '#components/Elements/SVGIcon/ContractCrossIcon';
import ContractTickIcon from '#components/Elements/SVGIcon/ContractTickIcon';
import ActivityTypeIcon from '#components/Elements/SVGIcon/ActivityTypeIcon';
import ChevronRightIcon from '#components/Elements/SVGIcon/ChevronRightIcon';
import Spinner from '#components/Elements/Spinner/Spinner';
import RelativeTime from '#components/Elements/RelativeTime/RelativeTime';
import EntityIcon from '#components/Elements/EntityAvatar/EntityIcon';
import EntityAvatar from '#components/Elements/EntityAvatar/EntityAvatar';
import InstrumentAmount from '#components/Elements/InstrumentAmount/InstrumentAmount';
import Toggle from '#components/Elements/Toggle/Toggle';
import MiddleDotSeparator from '#components/Elements/MiddleDotSeparator/MiddleDotSeparator';
import { useVisibleInstruments } from '#modules/api/Banking';
import styles from './ActivityTable.scss';

// In a debug build activity query errors are shown in the rows.
// In any build, a Sentry event for the error will be sent once per activity ID
// const SHOW_ERRORS = isDebugBuild;
const activityErrorsReported = new Set();

export const pendingFilter = ({ stage, terminationReason }) =>
  [
    'creating',
    'connecting',
    'confirming',
    'proposal',
    'reviewing',
    'accepting',
    'finalizing',
  ].includes(stage) && terminationReason == null;

const executedFilter = ({ stage, terminationReason }) =>
  stage === 'executed' && terminationReason == null;

const completeFilter = ({ stage, terminationReason }) =>
  executedFilter({ stage, terminationReason }) || terminationReason != null;

const historyFilter = ({ stage, terminationReason }) =>
  pendingFilter({ stage, terminationReason }) ||
  executedFilter({ stage, terminationReason });

const historyWithCancelledFilter = ({ stage, terminationReason }) =>
  historyFilter({ stage, terminationReason }) || terminationReason != null;

const everythingFilter = () => true;

function ActivityTable({ showHistoryForInstrumentId }) {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const { url, path } = useRouteMatch();
  const previousTab = useSelector((state) => state.tab['/overview/activity']);

  // if (isDebugBuild) {
  //   // Allows UI tests to reopen an in-progress activity details dialog
  //   // when the activity isn’t currently shown in the activity list
  //   window.testOpenActivityDetailsDialog = (activityId) => {
  //     if (activityId && history) {
  //       history.push(
  //         locationToOpenModal(location, modalRoutes.ACTIVITY_DETAILS, null, {
  //           id: activityId,
  //         }),
  //       );
  //     }
  //   };
  // }

  const queryParts = useMemo(
    () => queryString.parse(location?.search) || {},
    [location?.search],
  );

  let showCancelledRoute;
  if (queryParts.showCancelled === 'true') {
    showCancelledRoute = true;
  } else if (queryParts.showCancelled === 'false') {
    showCancelledRoute = false;
  }
  const previousShowCancelled = useSelector(
    (state) => state.activity?.showCancelled,
  );
  const showCancelled =
    showCancelledRoute === true ||
    (showCancelledRoute !== false && !!previousShowCancelled);

  useEffect(() => {
    if (previousShowCancelled !== showCancelled) {
      dispatch(setShowCancelledActivityAction(showCancelled));
    }
  }, [
    dispatch,
    previousShowCancelled,
    showCancelled,
    showHistoryForInstrumentId,
  ]);

  const toggleShowCancelled = () =>
    history.replace(
      locationToUpdateQueryString(location, {
        showCancelled: !showCancelled,
      }),
    );

  if (showCancelledRoute !== showCancelled) {
    return (
      <Redirect
        to={locationToUpdateQueryString(location, {
          showCancelled,
        })}
      />
    );
  }

  if (showHistoryForInstrumentId) {
    return (
      <ActivityTableHeader
        showHistoryForInstrumentId={showHistoryForInstrumentId}
        showCancelled={showCancelled}
        toggleShowCancelled={toggleShowCancelled}
      >
        <ActivityListInstrument
          instrumentId={showHistoryForInstrumentId}
          showCancelled={showCancelled}
        />
      </ActivityTableHeader>
    );
  }

  return (
    <Switch>
      <Route path={`${path}/activity-everything`}>
        <ActivityTableHeader url={url} path={path}>
          <ActivityListEverything />
        </ActivityTableHeader>
      </Route>
      <Route path={`${path}/activity-history`}>
        <ActivityTableHeader
          url={url}
          path={path}
          showCancelled={showCancelled}
          toggleShowCancelled={toggleShowCancelled}
          showExport={true}
        >
          <ActivityListHistory showCancelled={showCancelled} />
        </ActivityTableHeader>
      </Route>
      <Route path={`${path}/activity-open`}>
        <ActivityTableHeader
          url={url}
          path={path}
          showCancelled={showCancelled}
        >
          <ActivityListOpen />
        </ActivityTableHeader>
      </Route>
      <Route path={`${path}/activity-pending`}>
        <ActivityTableHeader
          url={url}
          path={path}
          showCancelled={showCancelled}
        >
          <ActivityListPending />
        </ActivityTableHeader>
      </Route>
      <Route path="*">
        <Redirect
          to={locationToUpdateMainPage(
            location,
            `${url}/${previousTab || 'activity-history'}`,
          )}
        />
      </Route>
    </Switch>
  );
}

function ActivityTableHeader({
  showHistoryForInstrumentId,
  url,
  children,
  showCancelled,
  showExport,
  toggleShowCancelled,
}) {
  const profileId = useSelectedProfileId();

  const openCount = useActivityCount({
    profileId,
    overviewFilter: pendingFilter,
    filters: {
      includeSuccessfulEvents: false,
      includeFailedEvents: false,
      includeEventsRequiringAction: true,
      includeEventsPendingOthers: false,
    },
  });
  const pendingCount = useActivityCount({
    profileId,
    overviewFilter: pendingFilter,
    filters: {
      includeSuccessfulEvents: false,
      includeFailedEvents: false,
      includeEventsRequiringAction: false,
      includeEventsPendingOthers: true,
    },
  });

  return (
    <>
      <TabHeader
        insertOnRight={
          <>
            {toggleShowCancelled ? ( // eslint-disable-next-line jsx-a11y/label-has-associated-control
              <label className={styles.cancelledFilter}>
                <span className={styles.cancelledLabel}>
                  <FormattedMessage
                    defaultMessage="Show cancelled"
                    description="Activity list filter control"
                  />
                </span>
                <Toggle
                  checked={showCancelled}
                  onChange={toggleShowCancelled}
                  data-test="show-cancelled"
                />
              </label>
            ) : null}
          </>
        }
      >
        {!showHistoryForInstrumentId && (
          <Tabs.NavTab
            data-test="activity-history"
            to={(location) =>
              locationToUpdateQueryString(
                locationToShowMainPage(location, `${url}/activity-history`),
                { showCancelled },
              )
            }
          >
            <FormattedMessage
              defaultMessage="History"
              description="Activity lists event history tab"
            />
          </Tabs.NavTab>
        )}
        {!showHistoryForInstrumentId && (
          <Tabs.NavTab
            data-test="activity-open"
            to={(location) =>
              locationToUpdateQueryString(
                locationToShowMainPage(location, `${url}/activity-open`),
                { showCancelled },
              )
            }
          >
            <FormattedMessage
              defaultMessage="Action items"
              description="Activity lists my action items tab"
            />
            {openCount ? (
              <TabBadge isUnread>{`${openCount}`}</TabBadge>
            ) : (
              <TabBadge>{'0'}</TabBadge>
            )}
          </Tabs.NavTab>
        )}
        {!showHistoryForInstrumentId && (
          <Tabs.NavTab
            data-test="activity-pending"
            to={(location) =>
              locationToUpdateQueryString(
                locationToShowMainPage(location, `${url}/activity-pending`),
                { showCancelled },
              )
            }
          >
            <FormattedMessage
              defaultMessage="Pending"
              description="Activity lists pending others tab"
            />
            {pendingCount ? (
              <TabBadge isUnread>{`${pendingCount}`}</TabBadge>
            ) : (
              <TabBadge>{'0'}</TabBadge>
            )}
          </Tabs.NavTab>
        )}
        {!!showHistoryForInstrumentId && (
          <Tabs.Tab active>
            <FormattedMessage
              defaultMessage="History"
              description="Activity lists specific instrument history tab"
            />
          </Tabs.Tab>
        )}
      </TabHeader>
      {children}
    </>
  );
}

function ActivityList({
  overviewFilter,
  tabName,
  filters,
  showDatePlaceholder,
  formatForDialogSummary,
  children,
  className,
}) {
  const profileId = useSelectedProfileId();
  const activity = useActivityOverview({ profileId, overviewFilter });

  const dispatch = useDispatch();
  useEffect(() => {
    if (tabName) {
      dispatch(onSetTab('/overview/activity', tabName));
    }
  }, [dispatch, tabName]);

  const [allLoaded, setAllLoaded] = useState(false);

  const lazyLoader = useMemo(
    () => loadVisible(20, 0, ({ allLoaded }) => setAllLoaded(allLoaded)),
    [],
  );

  if (!activity) {
    return <Spinner className={styles.loadingSpinner} />;
  }

  return (
    <div
      className={classNames(
        className,
        styles.activity,
        'scroll-under-floating-header',
      )}
      data-test={`${tabName}-table`}
    >
      <Table
        className={classNames(
          !activity?.length && styles.emptyTable,
          formatForDialogSummary && styles.formatForDialogSummary,
        )}
        fillWidth={formatForDialogSummary}
        data-loading={
          !!activity && (!activity?.length || allLoaded)
            ? 'activity-loaded'
            : 'activity-loading'
        }
      >
        <thead />

        {!!activity &&
          activity.map(({ activityId, lockboxId, dateModified }) => (
            <LazyActivityRow
              key={`${activityId}:${lockboxId}`}
              activityId={activityId}
              lockboxId={lockboxId}
              profileId={profileId}
              dateModified={dateModified}
              lazyLoader={lazyLoader}
              filters={filters}
              showDatePlaceholder={showDatePlaceholder}
              formatForDialogSummary={formatForDialogSummary}
            />
          ))}
        <tbody className={styles.onlyIfEmpty}>
          <tr className="empty">
            <td colSpan={formatForDialogSummary ? '2' : '5'}>{children}</td>
          </tr>
        </tbody>
      </Table>
    </div>
  );
}

function ActivityListEverything() {
  const appName = getAppName();
  return (
    <ActivityList
      tabName="activity-everything"
      overviewFilter={everythingFilter}
    >
      <div>
        <FormattedMessage
          defaultMessage="Welcome to {app}!"
          description="Activity history list empty state message title"
          values={{ app: appName }}
        />
      </div>
      <FormattedMessage
        defaultMessage="This is the activity feed for your entire workspace."
        description="Activity history list empty state message"
      />
    </ActivityList>
  );
}

function ActivityListHistory({ showCancelled }) {
  const appName = getAppName();
  return (
    <ActivityList
      tabName="activity-history"
      overviewFilter={showCancelled ? completeFilter : executedFilter}
      filters={{
        includeSuccessfulEvents: true,
        includeFailedEvents: showCancelled,
        includeEventsRequiringAction: false,
        includeEventsPendingOthers: false,
      }}
      showDatePlaceholder={true}
    >
      <div>
        <FormattedMessage
          defaultMessage="Welcome to {app}!"
          description="Activity history list empty state message title"
          values={{ app: appName }}
        />
      </div>
      <FormattedMessage
        defaultMessage="This is the activity feed for your entire workspace."
        description="Activity history list empty state message"
      />
    </ActivityList>
  );
}

function ActivityListOpen() {
  return (
    <ActivityList
      tabName="activity-open"
      overviewFilter={pendingFilter}
      filters={{
        includeSuccessfulEvents: false,
        includeFailedEvents: false,
        includeEventsRequiringAction: true,
        includeEventsPendingOthers: false,
      }}
    >
      <div>
        <FormattedMessage
          defaultMessage="All caught up"
          description="Activity open actions empty state message title"
        />
      </div>
      <FormattedMessage
        defaultMessage="No proposals, requests, or offers for you to take action on at this time."
        description="Activity open actions empty state message"
      />
    </ActivityList>
  );
}

function ActivityListPending() {
  return (
    <ActivityList
      tabName="activity-pending"
      overviewFilter={pendingFilter}
      filters={{
        includeSuccessfulEvents: false,
        includeFailedEvents: false,
        includeEventsRequiringAction: false,
        includeEventsPendingOthers: true,
      }}
    >
      <div>
        <FormattedMessage
          defaultMessage="No open items for others"
          description="Activity pending others list empty state message title"
        />
      </div>
      <FormattedMessage
        defaultMessage="Any pending requests and offers sent to other users will appear here."
        description="Activity pending others list empty state message"
      />
    </ActivityList>
  );
}

function ActivityListInstrument({ instrumentId, showCancelled }) {
  const profileId = useSelectedProfileId();
  const includeEventsForInstrument = useInstrumentWithDetails(
    instrumentId,
    profileId,
  );

  return (
    <ActivityList
      overviewFilter={
        showCancelled ? historyWithCancelledFilter : historyFilter
      }
      filters={{
        includeEventsForInstrument,
        includeSuccessfulEvents: true,
        includeFailedEvents: showCancelled,
        includeEventsRequiringAction: true,
        includeEventsPendingOthers: true,
      }}
    >
      <div>
        <FormattedMessage
          defaultMessage="Instrument activity history"
          description="Instrument activity history list empty state message title"
        />
      </div>
      <FormattedMessage
        defaultMessage="No activity found for this instrument."
        description="Instrument activity history list empty state message"
      />
    </ActivityList>
  );
}

export function ActivityListPendingWorkspaces() {
  return (
    <ActivityList
      formatForDialogSummary
      overviewFilter={overviewActivityPendingWorkspaceFilter}
      filters={{
        includeSuccessfulEvents: false,
        includeFailedEvents: false,
        includeEventsRequiringAction: true,
        includeEventsPendingOthers: true,
      }}
    />
  );
}

function ActivityCounterpartyIcon({ counterparty }) {
  return (
    <div className={styles.eventImageCell}>
      <EntityAvatar counterparty={counterparty} render={EntityIcon} />
    </div>
  );
}

function ActivityEventInfo({
  activityItem,
  subtitleParts,
  isAnonymous,
  enableLinks = true,
}) {
  return (
    <div className={classNames(styles.flexColumn)}>
      <div className={styles.eventTitle} data-test="event-description">
        {activityItem?.formatted?.description}
      </div>
      {(!!subtitleParts?.length || !!activityItem?.memo) && (
        <div
          className={classNames(
            styles.eventSubtitle,
            isAnonymous === false && styles.named,
          )}
          data-test="event-subtitle"
        >
          {!!subtitleParts?.length &&
            subtitleParts.map((part, partIndex) => {
              if (enableLinks) {
                if (part.type === 'entity' && part.handle) {
                  return (
                    <Link
                      key={`${part.type}${partIndex}`}
                      className={styles[`part-${part.type}`]}
                      to={(location) =>
                        locationToOpenModal(
                          location,
                          modalRoutes.ENTITY_PUBLIC_PROFILE,
                          undefined,
                          { handle: part.handle },
                        )
                      }
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                      data-description-part={part.type || undefined}
                      data-entity-handle={part.handle || undefined}
                    >
                      {part.value}
                    </Link>
                  );
                } else if (part.type === 'instrument' && part.instrumentId) {
                  return (
                    <Link
                      key={`${part.type}${partIndex}`}
                      className={styles[`part-${part.type}`]}
                      to={(location) =>
                        locationToShowInstrumentActivity(
                          location,
                          part.instrumentId,
                        )
                      }
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      {part.value}
                    </Link>
                  );
                }
              }
              return (
                <span
                  key={`${part.type}${partIndex}`}
                  className={styles[`part-${part.type}`]}
                  data-description-part={part.type || undefined}
                  data-entity-handle={part.handle || undefined}
                >
                  {part.value}
                </span>
              );
            })}
          {!!subtitleParts?.length && !!activityItem?.memo && (
            <MiddleDotSeparator />
          )}
          {!!activityItem?.memo && (
            <span data-description-part="memo">{activityItem.memo}</span>
          )}
        </div>
      )}
    </div>
  );
}

function ActivityDescriptions({ descriptions }) {
  const matchInstruments = useVisibleInstruments();
  return (
    descriptions?.map?.((description, index) => (
      <div key={`descriptions${index}`}>
        {description?.formatted?.descriptionParts?.map?.((part, partIndex) => {
          if (part.type === 'instrument' && part.instrumentId) {
            const matchedInstrument = matchInstruments?.find(
              (instrument) => instrument.instrument_id === part.instrumentId,
            );
            const displayName = matchedInstrument?.use_display_name
              ? matchedInstrument?.display_name
              : part.value;
            return (
              <Link
                key={`${part.type}${partIndex}`}
                className={styles[`part-${part.type}`]}
                to={(location) =>
                  locationToShowInstrumentActivity(location, part.instrumentId)
                }
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                {displayName}
              </Link>
            );
          } else if (part.type === 'contract' && part.contractAddress) {
            return (
              <Link
                key={`${part.type}${partIndex}`}
                className={styles[`part-${part.type}`]}
                to={(location) =>
                  locationToShowContract(location, part.contractAddress)
                }
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                {part.value}
              </Link>
            );
          } else if (
            (part.type === 'contract' || part.type === 'entity') &&
            part.handle
          ) {
            return (
              <Link
                key={`${part.type}${partIndex}`}
                className={styles[`part-${part.type}`]}
                to={(location) =>
                  locationToOpenModal(
                    location,
                    modalRoutes.ENTITY_PUBLIC_PROFILE,
                    undefined,
                    { handle: part.handle },
                  )
                }
                onClick={(event) => {
                  event.stopPropagation();
                }}
              >
                {part.value}
              </Link>
            );
          }
          return (
            <span
              key={`${part.type}${partIndex}`}
              className={styles[`part-${part.type}`]}
            >
              {part.value}
            </span>
          );
        })}
      </div>
    )) || null
  );
}

function ActivityTransferQuantities({ activityItem }) {
  const transfers = activityItem?.counterparties?.[0]?.transfers;

  return (
    transfers?.map?.((transfer, index) => (
      <InstrumentAmount
        key={`transfer${index}`}
        instrument={transfer.quantity}
        type="transfer"
        showCurrencyCodeOnRight
        renderAs="div"
        className={styles.quantity}
        test="transfer-amount"
      />
    )) || null
  );
}

function testAttributesForActivityRow(activityItem) {
  return {
    'data-test': 'activity-row',
    'data-activity-id': activityItem?.id,
    'data-activity-stage': activityItem?.stage,
    'data-activity-termination': activityItem?.terminationReason,
  };
}

function ActivityRowPayment({ activityItem, onShowDetails, notCounted }) {
  const counterparty = activityItem?.counterparties?.[0];
  const send = activityItem?.isInitiator;

  return (
    <tr
      className={classNames(
        styles.sentInstruments,
        notCounted && styles.notCounted,
      )}
      onClick={() => onShowDetails()}
      data-activity="payment"
      {...testAttributesForActivityRow(activityItem)}
    >
      <td className={styles.event}>
        <div className={styles.flexRow}>
          <ActivityTypeIcon
            className={styles.eventIcon}
            type="payment"
            isInitiator={send}
          />
          <ActivityCounterpartyIcon counterparty={counterparty} />
          <ActivityEventInfo
            activityItem={activityItem}
            subtitleParts={
              activityItem?.formatted?.feePayingInstrumentParts ||
              counterparty?.formatted?.descriptionParts
            }
            isAnonymous={counterparty?.isAnonymous}
          />
        </div>
      </td>
      <td className={styles.quantities}>
        <div className={styles.flexColumn}>
          <ActivityTransferQuantities activityItem={activityItem} />
        </div>
      </td>
      <td className={styles.eventDescription}>
        <div className={styles.flexColumn}>
          <ActivityDescriptions descriptions={counterparty?.transfers} />
        </div>
      </td>
      <td className={styles.timestamp}>
        <div className={styles.flexColumn}>
          <div>
            <RelativeTime
              test="date-modified"
              timestamp={activityItem.dateModified}
            />
          </div>
        </div>
      </td>
    </tr>
  );
}

function ActivityRowLockboxCreation({
  activityItem,
  onShowDetails,
  notCounted,
  formatForDialogSummary,
}) {
  return (
    <tr
      className={classNames(
        styles.sentInstruments,
        notCounted && styles.notCounted,
        formatForDialogSummary && styles.formatForDialogSummary,
      )}
      onClick={() => onShowDetails()}
      data-activity="workspace"
      {...testAttributesForActivityRow(activityItem)}
    >
      <td className={styles.event}>
        <div className={styles.flexRow}>
          {!formatForDialogSummary && (
            <Icon className={styles.eventIcon} icon="icon-plus" />
          )}
          <ActivityCounterpartyIcon counterparty={activityItem?.entity} />
          {formatForDialogSummary ? (
            <div className={classNames(styles.flexColumn)}>
              <div className={styles.eventTitle}>
                {activityItem?.entity?.displayName}
              </div>
              <div className={styles.eventSubtitle}>
                {activityItem?.formatted?.description}
              </div>
            </div>
          ) : (
            <ActivityEventInfo
              activityItem={activityItem}
              subtitleParts={activityItem?.formatted?.counterpartyParts}
              isAnonymous={activityItem?.entity?.isAnonymous}
              enableLinks={
                !notCounted &&
                (activityItem?.stage === 'agent_processing' ||
                  activityItem?.stage === 'executed')
              }
              formatForDialogSummary={formatForDialogSummary}
            />
          )}
        </div>
      </td>
      {!formatForDialogSummary && <td className={styles.quantities}></td>}
      <td className={styles.eventDescription}>
        {formatForDialogSummary ? (
          <div className={styles.rightChevron}>
            <ChevronRightIcon />
          </div>
        ) : (
          <div className={styles.flexColumn}>
            {activityItem?.formatted?.role}
          </div>
        )}
      </td>
      {!formatForDialogSummary && (
        <td className={classNames(styles.timestamp, styles.action)}>
          <div className={styles.flexColumn}>
            <div className={styles.buttons}></div>
            <div className={styles.when}>
              <RelativeTime
                test="date-modified"
                timestamp={activityItem.dateModified}
              />
            </div>
          </div>
        </td>
      )}
    </tr>
  );
}

function ActivityRowRequest({ onShowDetails, activityItem, notCounted }) {
  const counterparty = activityItem?.counterparties?.[0];
  const isInitiator = activityItem?.isInitiator;

  return (
    <tr
      className={classNames(
        styles.sentInstruments,
        notCounted && styles.notCounted,
      )}
      onClick={() => onShowDetails()}
      data-activity="request"
      {...testAttributesForActivityRow(activityItem)}
    >
      <td className={styles.event}>
        <div className={styles.flexRow}>
          <ActivityTypeIcon
            className={styles.eventIcon}
            type="payment"
            isInitiator={!isInitiator}
          />
          <ActivityCounterpartyIcon counterparty={counterparty} />
          <ActivityEventInfo
            activityItem={activityItem}
            subtitleParts={counterparty?.formatted?.descriptionParts}
            isAnonymous={counterparty?.isAnonymous}
          />
        </div>
      </td>
      <td className={styles.quantities}>
        <div className={styles.flexColumn}>
          <ActivityTransferQuantities activityItem={activityItem} />
        </div>
      </td>
      <td className={styles.eventDescription}>
        <div className={styles.flexColumn}>
          <ActivityDescriptions descriptions={counterparty?.transfers} />
        </div>
      </td>
      <td className={styles.timestamp}>
        <div className={styles.flexColumn}>
          <div>
            <RelativeTime
              test="date-modified"
              timestamp={activityItem.dateModified}
            />
          </div>
        </div>
      </td>
    </tr>
  );
}

function ActivityRowTrade({ onShowDetails, activityItem, notCounted }) {
  const counterparty = activityItem?.counterparties?.[0];
  const isInitiator = activityItem?.isInitiator;

  return (
    <tr
      className={classNames(
        styles.sentInstruments,
        notCounted && styles.notCounted,
      )}
      onClick={() => onShowDetails()}
      data-activity="trade"
      {...testAttributesForActivityRow(activityItem)}
    >
      <td className={styles.event}>
        <div className={styles.flexRow}>
          <ActivityTypeIcon
            className={styles.eventIcon}
            type="trade"
            isInitiator={isInitiator}
          />
          <ActivityCounterpartyIcon counterparty={counterparty} />
          <ActivityEventInfo
            activityItem={activityItem}
            subtitleParts={counterparty?.formatted?.descriptionParts}
            isAnonymous={counterparty?.isAnonymous}
          />
        </div>
      </td>
      <td className={styles.quantities}>
        <div className={styles.flexColumn}>
          <ActivityTransferQuantities activityItem={activityItem} />
        </div>
      </td>
      <td className={styles.eventDescription}>
        <div className={styles.flexColumn}>
          <ActivityDescriptions descriptions={counterparty?.transfers} />
        </div>
      </td>
      <td className={styles.timestamp}>
        <div className={styles.flexColumn}>
          <div>
            <RelativeTime
              test="date-modified"
              timestamp={activityItem.dateModified}
            />
          </div>
        </div>
      </td>
    </tr>
  );
}

function ActivityRowContractFormation({
  onShowDetails,
  activityItem,
  notCounted,
}) {
  let eventIcon = <ContractEditingIcon className={styles.eventIcon} />;
  if (activityItem?.terminationReason) {
    eventIcon = <ContractCrossIcon className={styles.eventIcon} />;
  } else if (
    activityItem?.stage === 'agent_processing' ||
    activityItem?.stage === 'executed'
  ) {
    eventIcon = <ContractTickIcon className={styles.eventIcon} />;
  }

  return (
    <tr
      className={classNames(
        styles.sentInstruments,
        notCounted && styles.notCounted,
      )}
      onClick={() => onShowDetails()}
      data-activity="contract-create"
      {...testAttributesForActivityRow(activityItem)}
    >
      <td className={styles.event}>
        <div className={styles.flexRow}>
          {eventIcon}
          <div className={styles.eventImageCell}>
            <EntityAvatar
              handle={activityItem?.contract?.issuerHandle}
              render={EntityIcon}
            />
          </div>
          <ActivityEventInfo
            activityItem={activityItem}
            subtitleParts={activityItem?.formatted?.initiatorParts}
            isAnonymous={false}
          />
        </div>
      </td>
      <td className={styles.quantities}></td>
      <td className={styles.eventDescription}>
        <div className={styles.flexColumn}>
          <ActivityDescriptions
            descriptions={activityItem?.contract && [activityItem.contract]}
          />
        </div>
      </td>
      <td className={styles.timestamp}>
        <div className={styles.flexColumn}>
          <div>
            <RelativeTime
              test="date-modified"
              timestamp={activityItem.dateModified}
            />
          </div>
        </div>
      </td>
    </tr>
  );
}

function ActivityRowContractAmendment({
  onShowDetails,
  activityItem,
  notCounted,
}) {
  let eventIcon = <ContractEditingIcon className={styles.eventIcon} />;
  if (activityItem?.terminationReason) {
    eventIcon = <ContractCrossIcon className={styles.eventIcon} />;
  }

  return (
    <tr
      className={classNames(
        styles.sentInstruments,
        notCounted && styles.notCounted,
      )}
      onClick={() => onShowDetails()}
      data-activity="contract-amend"
      {...testAttributesForActivityRow(activityItem)}
    >
      <td className={styles.event}>
        <div className={styles.flexRow}>
          {eventIcon}
          <div className={styles.eventImageCell}>
            <EntityAvatar
              handle={activityItem?.contract?.issuerHandle}
              render={EntityIcon}
            />
          </div>
          <ActivityEventInfo
            activityItem={activityItem}
            subtitleParts={activityItem?.formatted?.initiatorParts}
            isAnonymous={false}
          />
        </div>
      </td>
      <td className={styles.quantities}></td>
      <td className={styles.eventDescription}>
        <div className={styles.flexColumn}>
          <ActivityDescriptions
            descriptions={activityItem?.contract && [activityItem.contract]}
          />
        </div>
      </td>
      <td className={styles.timestamp}>
        <div className={styles.flexColumn}>
          <div>
            <RelativeTime
              test="date-modified"
              timestamp={activityItem.dateModified}
            />
          </div>
        </div>
      </td>
    </tr>
  );
}

function ActivityRowContractExpire({
  onShowDetails,
  activityItem,
  notCounted,
}) {
  return (
    <tr
      className={classNames(
        styles.sentInstruments,
        notCounted && styles.notCounted,
      )}
      onClick={() => onShowDetails()}
      data-activity="contract-expire"
      {...testAttributesForActivityRow(activityItem)}
    >
      <td className={styles.event}>
        <div className={styles.flexRow}>
          <ContractCrossIcon className={styles.eventIcon} />
          <div className={styles.eventImageCell}>
            <EntityAvatar
              handle={activityItem?.contract?.issuerHandle}
              render={EntityIcon}
            />
          </div>
          <ActivityEventInfo activityItem={activityItem} isAnonymous={false} />
        </div>
      </td>
      <td className={styles.quantities}></td>
      <td className={styles.eventDescription}>
        <div className={styles.flexColumn}>
          <ActivityDescriptions
            descriptions={activityItem?.contract && [activityItem.contract]}
          />
        </div>
      </td>
      <td className={styles.timestamp}>
        <div className={styles.flexColumn}>
          <div>
            <RelativeTime
              test="date-modified"
              timestamp={activityItem.dateModified}
            />
          </div>
        </div>
      </td>
    </tr>
  );
}

function ActivityRowInstrumentExpire({
  onShowDetails,
  activityItem,
  notCounted,
}) {
  return (
    <tr
      className={classNames(
        styles.sentInstruments,
        notCounted && styles.notCounted,
      )}
      onClick={() => onShowDetails()}
      data-activity="instrument-expire"
      {...testAttributesForActivityRow(activityItem)}
    >
      <td className={styles.event}>
        <div className={styles.flexRow}>
          <ContractCrossIcon className={styles.eventIcon} />
          <div className={styles.eventImageCell}>
            <EntityAvatar
              handle={activityItem?.contract?.issuerHandle}
              render={EntityIcon}
            />
          </div>
          <ActivityEventInfo activityItem={activityItem} isAnonymous={false} />
        </div>
      </td>
      <td className={styles.quantities}></td>
      <td className={styles.eventDescription}>
        <div className={styles.flexColumn}>
          <ActivityDescriptions descriptions={activityItem?.instruments} />
        </div>
      </td>
      <td className={styles.timestamp}>
        <div className={styles.flexColumn}>
          <div>
            <RelativeTime
              test="date-modified"
              timestamp={activityItem.dateModified}
            />
          </div>
        </div>
      </td>
    </tr>
  );
}

function ActivityRow({
  profileId,
  lockboxId,
  activityId,
  dateModified,
  filters = {},
  ...props
}) {
  const tokenizedApi = useTokenizedApi();
  const activityEventQueryResult = useActivityId({
    profileId,
    lockboxId,
    activityId,
    dateModified,
  });
  let activityItem = activityEventQueryResult?.data;
  if (activityItem && filters) {
    activityItem = tokenizedApi.activity.filterActivity(
      [activityItem],
      filters,
    )[0];
  }
  activityItem = useActivityEvent(activityItem);

  const history = useHistory();
  const location = useLocation();
  const onShowDetails = useCallback(
    (start) => {
      if (activityItem?.id) {
        history.push(
          locationToOpenModal(
            location,
            modalRoutes.ACTIVITY_DETAILS,
            { start },
            { id: activityItem.id },
          ),
        );
      }
    },
    [activityItem?.id, history, location],
  );

  const notCounted = !!activityItem?.terminationReason;

  if (activityEventQueryResult?.error) {
    if (activityId && !activityErrorsReported.has(activityId)) {
      const reportedError = new Error(
        `Activity ${activityId} error: ${activityEventQueryResult.error}`,
        {
          cause: activityEventQueryResult.error,
        },
      );
      console.error(reportedError);
      if (sentryDSN) {
        Sentry.captureException(reportedError, {
          tags: { activityId },
        });
      }
      activityErrorsReported.add(activityId);
    }
    // if (SHOW_ERRORS) {
    //   return (
    //     <tbody>
    //       <tr
    //         style={{ height: '64px' }}
    //         data-activity="error"
    //         {...testAttributesForActivityRow(activityItem)}
    //       >
    //         <td colSpan={4}>{`${activityEventQueryResult.error}`}</td>
    //       </tr>
    //     </tbody>
    //   );
    // }
  }
  if (!activityEventQueryResult || activityEventQueryResult.isLoading) {
    return (
      <tbody>
        <tr
          style={{ height: '64px' }}
          data-activity="loading"
          {...testAttributesForActivityRow(activityItem)}
        ></tr>
      </tbody>
    );
  }

  switch (activityItem?.activityEventType) {
    case 'lock_box_creation_proposal':
    case 'reorg':
      return (
        <tbody>
          <ActivityRowLockboxCreation
            {...props}
            activityItem={activityItem}
            onShowDetails={onShowDetails}
            notCounted={notCounted}
          />
        </tbody>
      );
    case 'payment':
    case 'transfer_fee':
      return (
        <tbody>
          <ActivityRowPayment
            {...props}
            activityItem={activityItem}
            onShowDetails={onShowDetails}
            notCounted={notCounted}
          />
        </tbody>
      );
    case 'trade_offer':
      if (activityItem?.isTransferRequest) {
        return (
          <tbody>
            <ActivityRowRequest
              {...props}
              activityItem={activityItem}
              onShowDetails={onShowDetails}
              notCounted={notCounted}
            />
          </tbody>
        );
      }
      return (
        <tbody>
          <ActivityRowTrade
            {...props}
            activityItem={activityItem}
            onShowDetails={onShowDetails}
            notCounted={notCounted}
          />
        </tbody>
      );
    case 'contract_formation':
      return (
        <tbody>
          <ActivityRowContractFormation
            {...props}
            activityItem={activityItem}
            onShowDetails={onShowDetails}
            notCounted={notCounted}
          />
        </tbody>
      );
    case 'contract_amendment':
      return (
        <tbody>
          <ActivityRowContractAmendment
            {...props}
            activityItem={activityItem}
            onShowDetails={onShowDetails}
            notCounted={notCounted}
          />
        </tbody>
      );
    case 'contract_expire':
      return (
        <tbody>
          <ActivityRowContractExpire
            {...props}
            activityItem={activityItem}
            onShowDetails={onShowDetails}
            notCounted={notCounted}
          />
        </tbody>
      );
    case 'instrument_expire':
      return (
        <tbody>
          <ActivityRowInstrumentExpire
            {...props}
            activityItem={activityItem}
            onShowDetails={onShowDetails}
            notCounted={notCounted}
          />
        </tbody>
      );
    default:
      return null;
  }
}

function ActivityRowPlaceholder({
  activityId,
  dateModified,
  showDate,
  formatForDialogSummary,
}) {
  if (formatForDialogSummary) {
    return (
      <tr
        data-test="activity-row"
        data-activity="placeholder"
        data-activity-id={activityId || undefined}
      >
        <td className={styles.event}></td>
        <td className={styles.eventDescription}></td>
      </tr>
    );
  }

  return (
    <tr
      data-test="activity-row"
      data-activity="placeholder"
      data-activity-id={activityId || undefined}
    >
      <td className={styles.event}></td>
      <td className={styles.quantities}></td>
      <td className={styles.eventDescription}></td>
      <td className={styles.timestamp}>
        <div className={styles.flexColumn}>
          <div>
            {showDate && (
              <RelativeTime test="date-modified" timestamp={dateModified} />
            )}
          </div>
        </div>
      </td>
    </tr>
  );
}

function LazyActivityRow({
  profileId,
  lockboxId,
  activityId,
  dateModified,
  lazyLoader,
  filters,
  showDatePlaceholder,
  formatForDialogSummary,
}) {
  const tokenizedApi = useTokenizedApi();

  const [loaded, setLoaded] = useState(false);

  if (loaded) {
    return (
      <ActivityRow
        profileId={profileId}
        lockboxId={lockboxId}
        activityId={activityId}
        dateModified={dateModified}
        filters={filters}
        formatForDialogSummary={formatForDialogSummary}
      />
    );
  }

  return (
    <tbody
      ref={lazyLoader(async () => {
        try {
          await tokenizedApi.activity.activityEventQuery.fetch({
            profileId,
            lockboxId,
            activityId,
            dateModified,
          });
        } catch (e) {
          console.error(e);
        }
        setLoaded(true);
      })}
    >
      <ActivityRowPlaceholder
        dateModified={dateModified}
        activityId={activityId}
        showDate={showDatePlaceholder}
        formatForDialogSummary={formatForDialogSummary}
      />
    </tbody>
  );
}

export default ActivityTable;
